import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Map as ReactMapGL, MapRef } from "react-map-gl";
import mapboxgl from "mapbox-gl";
import { useAppState } from "../../../AppContext";
import { MapContainer } from "../../Map/MapStyles";
import "mapbox-gl/dist/mapbox-gl.css";
import { EditorData, Picking, SelectedLink } from "../../../utils/editorUtils";
import { useEditState } from "../EditContext";
import { useMapData } from "./MapDataContext";
import { useCachedLayerData } from "./useCachedLayerData";
import { useRestAuth } from "../../../hooks/useRestAuth";
import SpinnerWrapper from "../BaseComponents/SpinnerWrapper";
import { MemoizedEditorPoiMarker } from "./EditorPoiMarker";
import { MemoizedEditorPanoMarker } from "./EditorPanoMarker";
import MapLinkEditor from "./Link/MapLinkEditor";
import { LinkEditorDialog } from "../EditorStyles";
import MapPickerPlaceholder from "./Link/MapPickerPlaceholder";
import MapAddButtons from "./MapAddButtons";
import { useMapState } from "../../../hooks/useMapState";
import { calculateViewport } from "../../../utils/mapUtils";
import WebMercatorViewport from "viewport-mercator-project";
import { LayerGroup } from "../../../types";
import { EditorScenarioSwitch } from "../BaseComponents/EditorScenarioSwitch";
import { useParams } from "react-router-dom";

// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

const customAttribution =
  '<a href="https://www.theimagineers.com/" target="_blank">© The Imagineers</a>';

interface MapEditorProps {
  layer?: LayerGroup;
}

const MapEditor: React.FC<MapEditorProps> = ({ layer }) => {
  const { state } = useAppState();
  const mapRef = useRef<MapRef>(null);
  const restAuth = useRestAuth();
  const mapState = useMapState();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [refreshingData, setRefreshingData] = useState(false);
  const { mapPanoRepository, mapPoiRepository, data, editData, target } = useEditState();
  const { updateLayerData, markAllLayersStale } = useMapData();
  const { data: cachedLayerData, loading: cacheLoading } = useCachedLayerData(target);
  const [editorData, setEditorData] = useState<EditorData<"map">>(data as EditorData<"map">);
  const [selectedLink, setSelectedLink] = useState<SelectedLink | undefined>(undefined);
  const [picking, setPicking] = useState(Picking.Off);
  const [linkEditorLoading, setLinkEditorLoading] = useState(false);
  const { scenarioSlug } = useParams<{ scenarioSlug: string }>();
  const [linkEditorCoordinatesLoading, setLinkEditorCoordinatesLoading] = useState(false);

  useEffect(() => {
    setEditorData(data as EditorData<"map">);
  }, [data]);

  const scenario = useMemo(() => {
    if (scenarioSlug && layer) {
      return layer.scenarios.find((s) => s.slug === scenarioSlug);
    }
  }, [layer, scenarioSlug]);

  // Use cached layer data when available for specific layers
  useEffect(() => {
    if (target && cachedLayerData) {
      setEditorData(cachedLayerData);
      setDataLoaded(true);
    } else if (!target) {
      // For global map view, use the data directly
      setEditorData(data as EditorData<"map">);
      setDataLoaded(true);
    } else if (target && cacheLoading) {
      // Layer data is being loaded
      setDataLoaded(false);
    } else if (target && !cachedLayerData) {
      // No cached data yet - keep loading
      setDataLoaded(false);
    }
  }, [target, cachedLayerData, data, cacheLoading]);

  useEffect(() => {
    /**
     * Fetches data for the global map view (all layers combined)
     */
    async function fetchGlobalMapData() {
      if (restAuth && !target) {
        setDataLoaded(false);
        setRefreshingData(true);

        // Fetch all data without layer filtering for global view
        const [panoLinks, poiLinks, unusedPanoLinks, unusedPoiLinks] = await Promise.all([
          mapPanoRepository.get(restAuth),
          mapPoiRepository.get(restAuth),
          mapPanoRepository.get_unused(restAuth),
          mapPoiRepository.get_unused(restAuth),
        ]);

        if (panoLinks || poiLinks) {
          const globalData = {
            panorama_location: panoLinks,
            poi_location: poiLinks,
            unlinked_panorama_location: [],
            unlinked_poi_location: [],
            unused_panorama_location: unusedPanoLinks,
            unused_poi_location: unusedPoiLinks,
          };
          editData<"map">(globalData);
          setEditorData(globalData);
        }

        setRefreshingData(false);
        setDataLoaded(true);
      }
    }

    fetchGlobalMapData();
  }, [editData, mapPanoRepository, mapPoiRepository, restAuth, target, setRefreshingData]);

  const [viewport, setViewport] = useState<any>(() => {
    const { innerWidth, innerHeight } = window;
    // Return if we have a state from the url
    if (mapState) {
      return new WebMercatorViewport({
        latitude: mapState.lat,
        longitude: mapState.long,
        zoom: mapState.zoom,
        width: innerWidth,
        height: innerHeight,
      });
    } else {
      return calculateViewport(state.map.initialBounds);
    }
  });

  const panoramaMarkers = useMemo(() => {
    return editorData.panorama_location
      ?.map((pano: any, index: number) => {
        const currentPano = state.panos.find((p) => p.id === pano.pano);
        if (!scenario || currentPano?.scenarios.some((s) => s.slug === scenario.slug)) {
          return (
            <MemoizedEditorPanoMarker
              key={`pano-${pano.pano}-${index}`}
              index={index}
              point={pano}
              selectedLink={selectedLink}
              setSelectedLink={setSelectedLink}
              setLinkEditorCoordinatesLoading={setLinkEditorCoordinatesLoading}
              setLinkEditorLoading={setLinkEditorLoading}
              setRefreshingData={setRefreshingData}
            />
          );
        }
        return null;
      })
      .filter(Boolean);
  }, [
    editorData.panorama_location,
    state.panos,
    scenario,
    selectedLink,
    setSelectedLink,
    setLinkEditorCoordinatesLoading,
    setLinkEditorLoading,
    setRefreshingData,
  ]);

  const poiMarkers = useMemo(() => {
    return editorData.poi_location
      ?.map((poi: any, index: number) => {
        const currentPoi = state.pois.find((p) => p.id === poi.poi);
        if (!scenario || currentPoi?.scenarios.some((s) => s === scenario.id)) {
          return (
            <MemoizedEditorPoiMarker
              key={`poi-${poi.poi}-${index}`}
              index={index}
              point={poi}
              selectedLink={selectedLink}
              setSelectedLink={setSelectedLink}
              setLinkEditorCoordinatesLoading={setLinkEditorCoordinatesLoading}
              setLinkEditorLoading={setLinkEditorLoading}
              setRefreshingData={setRefreshingData}
            />
          );
        }
        return null;
      })
      .filter(Boolean);
  }, [
    editorData.poi_location,
    state.pois,
    scenario,
    selectedLink,
    setSelectedLink,
    setLinkEditorCoordinatesLoading,
    setLinkEditorLoading,
    setRefreshingData,
  ]);

  const unlinkedPanoramaMarkers = useMemo(() => {
    return editorData.unlinked_panorama_location
      ?.map((pano: any, index: number) => {
        const currentPano = state.panos.find((p) => p.id === pano.pano);
        if (!scenario || currentPano?.scenarios.some((s) => s.slug === scenario.slug)) {
          return (
            <MemoizedEditorPanoMarker
              key={`unlinked-pano-${pano.pano}-${index}`}
              index={index}
              point={pano}
              selectedLink={selectedLink}
              setSelectedLink={setSelectedLink}
              setLinkEditorCoordinatesLoading={setLinkEditorCoordinatesLoading}
              setLinkEditorLoading={setLinkEditorLoading}
              setRefreshingData={setRefreshingData}
              unlinked={true}
            />
          );
        }
        return null;
      })
      .filter(Boolean);
  }, [
    editorData.unlinked_panorama_location,
    state.panos,
    scenario,
    selectedLink,
    setSelectedLink,
    setLinkEditorCoordinatesLoading,
    setLinkEditorLoading,
    setRefreshingData,
  ]);

  const unlinkedPoiMarkers = useMemo(() => {
    return editorData.unlinked_poi_location
      ?.map((poi: any, index: number) => {
        const currentPoi = state.pois.find((p) => p.id === poi.poi);
        if (!scenario || currentPoi?.scenarios.some((s) => s === scenario.id)) {
          return (
            <MemoizedEditorPoiMarker
              key={`unlinked-poi-${poi.poi}-${index}`}
              index={index}
              point={poi}
              selectedLink={selectedLink}
              setSelectedLink={setSelectedLink}
              setLinkEditorCoordinatesLoading={setLinkEditorCoordinatesLoading}
              setLinkEditorLoading={setLinkEditorLoading}
              setRefreshingData={setRefreshingData}
              unlinked={true}
            />
          );
        }
        return null;
      })
      .filter(Boolean);
  }, [
    editorData.unlinked_poi_location,
    state.pois,
    scenario,
    selectedLink,
    setSelectedLink,
    setLinkEditorCoordinatesLoading,
    setLinkEditorLoading,
    setRefreshingData,
  ]);

  const addLink = useCallback(
    async (lat: number, long: number) => {
      const pickingCopy = picking;
      setPicking(Picking.Off);
      setLinkEditorLoading(true);
      setRefreshingData(true);

      if (pickingCopy === Picking.Pano && restAuth) {
        const previous_unused_links = editorData.unused_panorama_location;
        await mapPanoRepository.create(restAuth, { long, lat }, target);

        if (target) {
          // For specific layer: fetch all layer data again
          const panoLinks = await mapPanoRepository.get(restAuth, target);
          const poiLinks = await mapPoiRepository.get(restAuth, target);
          const unlinkedPanoLinks = await mapPanoRepository.get_unlinked(restAuth, target);
          const unlinkedPoiLinks = await mapPoiRepository.get_unlinked(restAuth, target);
          const unusedPanoLinks = await mapPanoRepository.get_unused(restAuth);
          const unusedPoiLinks = await mapPoiRepository.get_unused(restAuth);

          const refreshedData = {
            panorama_location: panoLinks,
            poi_location: poiLinks,
            unlinked_panorama_location: unlinkedPanoLinks,
            unlinked_poi_location: unlinkedPoiLinks,
            unused_panorama_location: unusedPanoLinks,
            unused_poi_location: unusedPoiLinks,
          };

          // Update cache with fresh data
          updateLayerData(target, refreshedData);
          setEditorData(refreshedData);
        } else {
          // For global data: fetch all global data again and clear layer cache
          const panoLinks = await mapPanoRepository.get(restAuth);
          const poiLinks = await mapPoiRepository.get(restAuth);
          const unusedPanoLinks = await mapPanoRepository.get_unused(restAuth);
          const unusedPoiLinks = await mapPoiRepository.get_unused(restAuth);

          const refreshedData = {
            panorama_location: panoLinks,
            poi_location: poiLinks,
            unlinked_panorama_location: [],
            unlinked_poi_location: [],
            unused_panorama_location: unusedPanoLinks,
            unused_poi_location: unusedPoiLinks,
          };

          // Mark layers as stale since global changes affect all layers
          markAllLayersStale();
          editData<"map">(refreshedData);
          setEditorData(refreshedData);
        }

        // Find the newly created link to select it
        const currentUnusedLinks = target
          ? await mapPanoRepository.get_unused(restAuth)
          : editorData.unused_panorama_location;
        const newLink = previous_unused_links.find(
          (link) => !currentUnusedLinks.some((unusedLink) => unusedLink.pano === link.pano)
        );
        if (newLink) {
          setSelectedLink({ type: "pano", item: newLink.pano });
        }
      }

      if (pickingCopy === Picking.Poi && restAuth) {
        const previous_unused_links = editorData.unused_poi_location;
        await mapPoiRepository.create(restAuth, { lat, long }, target);

        if (target) {
          // For specific layer: fetch all layer data again
          const panoLinks = await mapPanoRepository.get(restAuth, target);
          const poiLinks = await mapPoiRepository.get(restAuth, target);
          const unlinkedPanoLinks = await mapPanoRepository.get_unlinked(restAuth, target);
          const unlinkedPoiLinks = await mapPoiRepository.get_unlinked(restAuth, target);
          const unusedPanoLinks = await mapPanoRepository.get_unused(restAuth);
          const unusedPoiLinks = await mapPoiRepository.get_unused(restAuth);

          const refreshedData = {
            panorama_location: panoLinks,
            poi_location: poiLinks,
            unlinked_panorama_location: unlinkedPanoLinks,
            unlinked_poi_location: unlinkedPoiLinks,
            unused_panorama_location: unusedPanoLinks,
            unused_poi_location: unusedPoiLinks,
          };

          // Update cache with fresh data
          updateLayerData(target, refreshedData);
          setEditorData(refreshedData);
        } else {
          // For global data: fetch all global data again and clear layer cache
          const panoLinks = await mapPanoRepository.get(restAuth);
          const poiLinks = await mapPoiRepository.get(restAuth);
          const unusedPanoLinks = await mapPanoRepository.get_unused(restAuth);
          const unusedPoiLinks = await mapPoiRepository.get_unused(restAuth);

          const refreshedData = {
            panorama_location: panoLinks,
            poi_location: poiLinks,
            unlinked_panorama_location: [],
            unlinked_poi_location: [],
            unused_panorama_location: unusedPanoLinks,
            unused_poi_location: unusedPoiLinks,
          };

          // Mark layers as stale since global changes affect all layers
          markAllLayersStale();
          editData<"map">(refreshedData);
          setEditorData(refreshedData);
        }

        // Find the newly created link to select it
        const currentUnusedLinks = target
          ? await mapPoiRepository.get_unused(restAuth)
          : editorData.unused_poi_location;
        const newLink = previous_unused_links.find(
          (link) => !currentUnusedLinks.some((unusedLink) => unusedLink.poi === link.poi)
        );
        if (newLink) {
          setSelectedLink({ type: "poi", item: newLink.poi });
        }
      }

      setRefreshingData(false);
      setLinkEditorLoading(false);
    },
    [
      editData,
      editorData.unused_panorama_location,
      editorData.unused_poi_location,
      mapPanoRepository,
      mapPoiRepository,
      picking,
      restAuth,
      target,
      updateLayerData,
      setRefreshingData,
      markAllLayersStale,
    ]
  );

  return (
    <SpinnerWrapper loading={!dataLoaded || !state.map || !state.map.accessToken || refreshingData}>
      <MapContainer style={{ width: "100%", height: "100dvh" }}>
        <ReactMapGL
          {...viewport}
          key={state.map.accessToken}
          ref={mapRef}
          customAttribution={customAttribution}
          mapboxAccessToken={state.map.accessToken}
          mapStyle={state.map.style}
          minZoom={state.mapConfig.minZoom}
          maxZoom={state.mapConfig.maxZoom}
          onMove={(e) => setViewport(e.viewState)}
          onClick={async (event) => {
            if (picking !== Picking.Off && !target) {
              addLink(event.lngLat.lat, event.lngLat.lng);
            }
          }}
        >
          {!target ? (
            <>
              {picking !== Picking.Off && (
                <LinkEditorDialog>
                  <MapPickerPlaceholder setPicking={setPicking} />
                </LinkEditorDialog>
              )}
              {picking === Picking.Off && (
                <>
                  {selectedLink ? (
                    <LinkEditorDialog>
                      <SpinnerWrapper loading={linkEditorLoading}>
                        <MapLinkEditor
                          selectedLink={selectedLink}
                          onCloseEditor={() => setSelectedLink(undefined)}
                          setSelectedLink={setSelectedLink}
                          coordinateInputsLoading={linkEditorCoordinatesLoading}
                        />
                      </SpinnerWrapper>
                    </LinkEditorDialog>
                  ) : !linkEditorLoading ? (
                    <MapAddButtons setPicking={setPicking} />
                  ) : (
                    <LinkEditorDialog>
                      <SpinnerWrapper loading={linkEditorLoading}>
                        <div></div>
                      </SpinnerWrapper>
                    </LinkEditorDialog>
                  )}
                </>
              )}
            </>
          ) : layer && scenario ? (
            <EditorScenarioSwitch item={layer} selectedScenario={scenario} />
          ) : null}
          {panoramaMarkers}
          {poiMarkers}
          {unlinkedPanoramaMarkers}
          {unlinkedPoiMarkers}
        </ReactMapGL>
      </MapContainer>
    </SpinnerWrapper>
  );
};

export default MapEditor;
